import React, { useState, useEffect } from "react";
import { 
    Card, Table, Row, Col, Badge, Spinner, Alert, Button,
    InputGroup, Form, ProgressBar, OverlayTrigger, Tooltip
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { formatCurrency } from "../utils/formatters";

export default function Surstock() {
    const navigate = useNavigate();
    const [produits, setProduits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [filterCategorie, setFilterCategorie] = useState('');
    const [categories, setCategories] = useState([]);

    useEffect(() => {
        fetchProduits();
        fetchCategories();
    }, [filterCategorie]);

    const fetchProduits = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            let url = 'https://sawd.julesferry-ga.com/api/stock_alertes.php?action=surstock';
            
            const params = new URLSearchParams();
            if (filterCategorie) params.append('categorie', filterCategorie);
            if (params.toString()) url += '&' + params.toString();
            
            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setProduits(result.data || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/stock_alertes.php?action=categories',
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                setCategories(result.data || []);
            }
        } catch (err) {
            console.error("Erreur catégories", err);
        }
    };

    const filteredProduits = produits.filter(p => 
        p.reference?.toLowerCase().includes(filterText.toLowerCase()) ||
        p.designation?.toLowerCase().includes(filterText.toLowerCase())
    );

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="info" />
            </div>
        );
    }

    if (error) {
        return <Alert variant="danger">{error}</Alert>;
    }

    return (
        <>
            {/* Filtres */}
            <Row className="g-3 mb-4">
                <Col md={5}>
                    <InputGroup>
                        <InputGroup.Text><i className="ri-search-line"></i></InputGroup.Text>
                        <Form.Control
                            placeholder="Rechercher un produit..."
                            value={filterText}
                            onChange={(e) => setFilterText(e.target.value)}
                        />
                    </InputGroup>
                </Col>
                <Col md={4}>
                    <Form.Select 
                        value={filterCategorie}
                        onChange={(e) => setFilterCategorie(e.target.value)}
                    >
                        <option value="">Toutes catégories</option>
                        {categories.map(c => (
                            <option key={c.id_categorie} value={c.id_categorie}>
                                {c.nom_categorie}
                            </option>
                        ))}
                    </Form.Select>
                </Col>
                <Col md={3}>
                    <Button variant="outline-secondary" onClick={() => {
                        setFilterText('');
                        setFilterCategorie('');
                    }} className="w-100">
                        <i className="ri-refresh-line me-2"></i>Réinitialiser
                    </Button>
                </Col>
            </Row>

            {/* Tableau */}
            <div className="table-responsive">
                <Table hover className="mb-0 align-middle">
                    <thead className="bg-light">
                        <tr>
                            <th>Réf.</th>
                            <th>Produit</th>
                            <th>Catégorie</th>
                            <th className="text-center">Stock actuel</th>
                            <th className="text-center">Max recommandé</th>
                            <th className="text-center">Excédent</th>
                            <th className="text-center">Utilisation</th>
                            <th>Valeur immobilisée</th>
                            <th className="text-center">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredProduits.length > 0 ? (
                            filteredProduits.map((p, index) => {
                                const ratio = (p.stock_maximum / p.quantite_reelle) * 100;
                                return (
                                    <tr key={p.id_produit} style={{
                                        animation: `fadeIn 0.3s ease ${index * 0.03}s both`
                                    }}>
                                        <td>
                                            <Badge bg="light" text="dark">{p.reference}</Badge>
                                        </td>
                                        <td>
                                            <Link to={`/stock/produits/${p.id_produit}`} className="text-primary">
                                                {p.designation}
                                            </Link>
                                        </td>
                                        <td>{p.nom_categorie || '-'}</td>
                                        <td className="text-center fw-bold text-info">{p.quantite_reelle}</td>
                                        <td className="text-center">{p.stock_maximum}</td>
                                        <td className="text-center">
                                            <span className="fw-bold text-warning">
                                                +{p.quantite_excedent}
                                            </span>
                                        </td>
                                        <td className="text-center" style={{ minWidth: '120px' }}>
                                            <div className="d-flex align-items-center gap-2">
                                                <ProgressBar 
                                                    now={ratio} 
                                                    variant="info"
                                                    style={{ width: '80px', height: '8px' }}
                                                />
                                                <small className="text-muted">{ratio.toFixed(0)}%</small>
                                            </div>
                                        </td>
                                        <td className="fw-bold text-primary">{formatCurrency(p.valeur_stock)}</td>
                                        <td className="text-center">
                                            <OverlayTrigger placement="top" overlay={<Tooltip>Voir le produit</Tooltip>}>
                                                <Button 
                                                    variant="outline-info" 
                                                    size="sm"
                                                    className="rounded-circle"
                                                    style={{ width: '32px', height: '32px' }}
                                                    onClick={() => navigate(`/stock/produits/${p.id_produit}`)}
                                                >
                                                    <i className="ri-eye-line"></i>
                                                </Button>
                                            </OverlayTrigger>
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan="9" className="text-center py-5">
                                    <i className="ri-checkbox-circle-line fs-1 text-success mb-3"></i>
                                    <h5>Aucun produit en surstock</h5>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </div>

            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </>
    );
}